import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import Navbar from "@/components/Navbar";
import {
  ChevronLeft, CheckCircle, ArrowRight, ArrowLeft, Info,
  User, Briefcase, Shield, Clock, Activity
} from "lucide-react";

import { API } from "../lib/api";

const ROLES = [
  {
    id: "individual_contributor",
    label: "Individual Contributor",
    desc: "Team member or individual project contributor",
    questions: 15,
    icon: User,
    time: "~5 min",
  },
  {
    id: "manager",
    label: "Project Manager",
    desc: "Managing teams, resources, and project delivery",
    questions: 45,
    icon: Briefcase,
    time: "~15 min",
  },
  {
    id: "executive",
    label: "Executive / Leader",
    desc: "Strategic oversight and organizational leadership",
    questions: 65,
    icon: Shield,
    time: "~20 min",
  },
];

function shuffleArray(arr) {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export default function NewProjectPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState("role"); // "role" | "details" | "assessment" | "feedback"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [responses, setResponses] = useState({});
  const [selectedRole, setSelectedRole] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [questionTimers, setQuestionTimers] = useState({});
  const [questionStartTime, setQuestionStartTime] = useState(null);

  const [form, setForm] = useState({
    name: "",
    description: "",
    project_type: "IT",
    timeline_pressure: "Medium",
    stakeholder_complexity: "Medium",
    team_size: 10,
    stress_level: "Medium",
    deadline_pressure: "Normal",
    assessment_scope: "individual",
    // ─── SYNQ Stage Awareness ───
    project_stage: "active_execution",
    involves_financial_management: false,
    sprint_pulse_cadence_days: 14,
    // ─── Experience Context Layer ───
    total_experience: "3-5",
    relevant_experience: "1-3",
    // ─── Open Feedback Intelligence ───
    positive_feedback: "",
    improvement_feedback: "",
  });
  const [stageInfo, setStageInfo] = useState(null);
  const [fatigueLevel, setFatigueLevel] = useState(null);

  // Auto-select role based on classified_role from employee record
  useEffect(() => {
    if (user?.classified_role && !selectedRole) {
      setSelectedRole(user.classified_role);
    }
  }, [user, selectedRole]);

  // Fetch and shuffle questions when role is selected. Uses SYNQ stage + financial toggle.
  const fetchQuestions = useCallback(async (role) => {
    try {
      const params = new URLSearchParams({
        role,
        stage: form.project_stage,
        financial: form.involves_financial_management ? "true" : "false",
      });
      const { data } = await axios.get(`${API}/api/questions?${params.toString()}`, { withCredentials: true });
      const list = Array.isArray(data) ? data : (data.questions || []);
      // Shuffle questions and shuffle options within each question (anti-manipulation)
      const shuffled = shuffleArray(list).map((q) => ({
        ...q,
        options: shuffleArray(q.options),
      }));
      setQuestions(shuffled);
    } catch (err) {
      console.error("Failed to load questions", err);
    }
  }, [form.project_stage, form.involves_financial_management]);

  // Fetch stage metadata (mode labels) for badge display
  useEffect(() => {
    axios.get(`${API}/api/stages`, { withCredentials: true })
      .then(({ data }) => {
        const found = data.stages?.find((s) => s.id === form.project_stage);
        if (found) setStageInfo({ system_mode: found.system_mode, mode_label: found.mode_label, stage: found.id });
      })
      .catch(() => {});
  }, [form.project_stage]);

  // Fetch survey fatigue indicator on mount
  useEffect(() => {
    axios.get(`${API}/api/synq/fatigue`, { withCredentials: true })
      .then(({ data }) => setFatigueLevel(data))
      .catch(() => {});
  }, []);

  const totalQ = questions.length;
  const answeredCount = Object.keys(responses).length;
  const progressPct = totalQ > 0 ? Math.round((answeredCount / totalQ) * 100) : 0;
  const allAnswered = totalQ > 0 && answeredCount === totalQ;
  const currentQuestion = questions[currentQ];

  const handleSelectRole = (roleId) => {
    setSelectedRole(roleId);
    setError("");
  };

  const proceedToDetails = () => {
    if (!selectedRole) { setError("Please select your assessment level"); return; }
    setError("");
    setStep("details");
    fetchQuestions(selectedRole);
  };

  const submitDetails = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Project name is required."); return; }
    setError("");
    // Re-fetch questions based on selected stage + financial toggle before starting assessment
    await fetchQuestions(selectedRole);
    setResponses({}); // clear answers when bank changes
    setStep("assessment");
    setCurrentQ(0);
    setQuestionStartTime(Date.now());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAnswer = (qId, label) => {
    // Track time spent on this question
    if (questionStartTime) {
      const elapsed = Math.round((Date.now() - questionStartTime) / 1000);
      setQuestionTimers((prev) => ({ ...prev, [qId]: elapsed }));
    }
    setResponses((prev) => ({ ...prev, [qId]: label }));
    // Auto-advance to next question after brief delay
    setTimeout(() => {
      if (currentQ < totalQ - 1) {
        setCurrentQ(currentQ + 1);
        setQuestionStartTime(Date.now());
      }
    }, 300);
  };

  const goToQuestion = (idx) => {
    if (questionStartTime && currentQuestion) {
      const elapsed = Math.round((Date.now() - questionStartTime) / 1000);
      setQuestionTimers((prev) => ({ ...prev, [currentQuestion.id]: elapsed }));
    }
    setCurrentQ(idx);
    setQuestionStartTime(Date.now());
  };

  const submitAssessment = async () => {
    if (!allAnswered) { setError(`Please answer all ${totalQ} questions.`); return; }
    setError("");
    setLoading(true);
    try {
      const payload = { ...form, assessment_role: selectedRole, responses };
      const { data } = await axios.post(`${API}/api/projects`, payload, { withCredentials: true });
      navigate(`/project/${data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || "Analysis failed. Please try again.");
      setLoading(false);
    }
  };

  // ─── Step 1: Role Selection ─────────────────────────────────────────────
  if (step === "role") {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-8">
          <div className="mb-8 animate-slide-up">
            <button onClick={() => navigate("/dashboard")}
              data-testid="back-to-dashboard"
              className="flex items-center gap-1 text-sm font-ibm-plex text-slate-500 hover:text-slate-900 mb-4 transition-colors">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <h1 className="font-outfit text-3xl font-black text-slate-900">New SYNQ Assessment</h1>
            <p className="font-ibm-plex text-sm text-slate-500 mt-1">
              Step 1 of 3 — Select your assessment level
            </p>
          </div>

          {/* Steps indicator */}
          <div className="flex items-center gap-3 mb-8 animate-slide-up stagger-1">
            {[
              { num: 1, label: "Role", active: true },
              { num: 2, label: "Project Details", active: false },
              { num: 3, label: "Assessment", active: false },
            ].map((s, i) => (
              <div key={s.num} className="flex items-center gap-2 flex-1">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-outfit font-black ${
                  s.active ? "bg-slate-900 text-white" : "bg-gray-200 text-slate-400"
                }`}>{s.num}</div>
                <span className={`font-ibm-plex text-sm ${s.active ? "font-medium text-slate-900" : "text-slate-400"}`}>
                  {s.label}
                </span>
                {i < 2 && <div className="flex-1 h-px bg-gray-200" />}
              </div>
            ))}
          </div>

          <div className="space-y-3 animate-slide-up stagger-2">
            <p className="font-ibm-plex text-sm text-slate-600 mb-4">
              Your role determines the assessment depth. SYNQ adapts its diagnostic intelligence based on your organizational perspective.
            </p>
            {ROLES.map((role) => {
              const Icon = role.icon;
              const isSelected = selectedRole === role.id;
              return (
                <button
                  key={role.id}
                  data-testid={`role-${role.id}`}
                  onClick={() => handleSelectRole(role.id)}
                  className={`w-full text-left p-5 rounded-xl border-2 transition-all ${
                    isSelected
                      ? "border-slate-900 bg-slate-50 shadow-sm"
                      : "border-gray-200 hover:border-slate-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isSelected ? "bg-slate-900 text-white" : "bg-gray-100 text-slate-500"
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-outfit font-bold text-slate-900">{role.label}</span>
                        <div className="flex items-center gap-3">
                          <span className="font-ibm-plex text-xs text-slate-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {role.time}
                          </span>
                          <span className={`font-outfit text-sm font-bold ${isSelected ? "text-slate-900" : "text-slate-400"}`}>
                            {role.questions}Q
                          </span>
                        </div>
                      </div>
                      <p className="font-ibm-plex text-sm text-slate-500">{role.desc}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
                      isSelected ? "border-slate-900 bg-slate-900" : "border-gray-300"
                    }`}>
                      {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 font-ibm-plex">
              {error}
            </div>
          )}

          <div className="flex gap-3 mt-8 animate-slide-up stagger-3">
            <button type="button" onClick={() => navigate("/dashboard")}
              className="flex-1 border border-gray-300 text-slate-700 font-outfit font-semibold py-3 rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button
              data-testid="proceed-to-details"
              onClick={proceedToDetails}
              className={`flex-1 font-outfit font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                selectedRole ? "bg-slate-900 text-white hover:bg-slate-800" : "bg-gray-200 text-slate-400 cursor-not-allowed"
              }`}
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Step 2: Project Details ─────────────────────────────────────────────
  if (step === "details") {
    const roleInfo = ROLES.find((r) => r.id === selectedRole);
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-8">
          <div className="mb-8 animate-slide-up">
            <button onClick={() => setStep("role")}
              className="flex items-center gap-1 text-sm font-ibm-plex text-slate-500 hover:text-slate-900 mb-4 transition-colors">
              <ChevronLeft className="w-4 h-4" /> Back to Role Selection
            </button>
            <h1 className="font-outfit text-3xl font-black text-slate-900">New SYNQ Assessment</h1>
            <p className="font-ibm-plex text-sm text-slate-500 mt-1">
              Step 2 of 3 — Project details ({roleInfo?.label} · {roleInfo?.questions} questions)
            </p>
          </div>

          {/* Steps indicator */}
          <div className="flex items-center gap-3 mb-8 animate-slide-up stagger-1">
            {[
              { num: 1, label: "Role", done: true },
              { num: 2, label: "Project Details", active: true },
              { num: 3, label: "Assessment", active: false },
            ].map((s, i) => (
              <div key={s.num} className="flex items-center gap-2 flex-1">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-outfit font-black ${
                  s.done ? "bg-emerald-500 text-white" : s.active ? "bg-slate-900 text-white" : "bg-gray-200 text-slate-400"
                }`}>{s.done ? <CheckCircle className="w-4 h-4" /> : s.num}</div>
                <span className={`font-ibm-plex text-sm ${s.active || s.done ? "font-medium text-slate-900" : "text-slate-400"}`}>
                  {s.label}
                </span>
                {i < 2 && <div className="flex-1 h-px bg-gray-200" />}
              </div>
            ))}
          </div>

          <form onSubmit={submitDetails} className="space-y-5">
            <div className="bg-white border border-gray-200 rounded-xl p-6 animate-slide-up stagger-2">
              <div className="space-y-4">
                {/* Assessment Scope */}
                <div>
                  <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">Assessment Scope</label>
                  <div className="flex gap-3">
                    {[{v: "individual", l: "Individual Project"}, {v: "organization", l: "Organization-wide"}].map((s) => (
                      <button key={s.v} type="button" data-testid={`scope-${s.v}`}
                        onClick={() => setForm({ ...form, assessment_scope: s.v })}
                        className={`flex-1 py-2.5 rounded-lg border-2 text-sm font-outfit font-medium transition-all ${
                          form.assessment_scope === s.v ? "border-slate-900 bg-slate-50 text-slate-900" : "border-gray-200 text-slate-500 hover:border-slate-300"
                        }`}>{s.l}</button>
                    ))}
                  </div>
                </div>

                {/* SYNQ Project Stage */}
                <div data-testid="project-stage-section">
                  <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                    Project Stage
                    <span className="ml-2 text-[10px] font-normal text-slate-400 normal-case tracking-normal">— drives SYNQ sensing mode</span>
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { v: "pre_project",      l: "Pre-Project",       sub: "Calibration Mode" },
                      { v: "active_execution", l: "Active Execution",  sub: "Sensing Mode" },
                      { v: "sprint_mode",      l: "Sprint Mode",       sub: "Pulse Mode" },
                    ].map((s) => (
                      <button key={s.v} type="button" data-testid={`stage-${s.v}`}
                        onClick={() => setForm({ ...form, project_stage: s.v })}
                        className={`text-left py-2.5 px-3 rounded-lg border-2 transition-all ${
                          form.project_stage === s.v ? "border-slate-900 bg-slate-50" : "border-gray-200 hover:border-slate-300"
                        }`}>
                        <div className={`font-outfit text-sm font-semibold ${form.project_stage === s.v ? "text-slate-900" : "text-slate-700"}`}>{s.l}</div>
                        <div className="font-ibm-plex text-[11px] text-slate-400 mt-0.5">{s.sub}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Financial Intelligence toggle */}
                <div data-testid="financial-toggle-section" className="flex items-start gap-3 p-3 bg-slate-50 border border-gray-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="involves-financial"
                    data-testid="involves-financial-toggle"
                    checked={form.involves_financial_management}
                    onChange={(e) => setForm({ ...form, involves_financial_management: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-slate-900 cursor-pointer"
                  />
                  <label htmlFor="involves-financial" className="flex-1 cursor-pointer">
                    <div className="font-outfit text-sm font-semibold text-slate-900">Involves financial management</div>
                    <div className="font-ibm-plex text-xs text-slate-500 mt-0.5">
                      Adds budget, spend-pressure, and funding-resilience signals — mapped to existing pillars.
                    </div>
                  </label>
                </div>

                {/* Sprint cadence (only when sprint_mode) */}
                {form.project_stage === "sprint_mode" && (
                  <div data-testid="sprint-cadence-section">
                    <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                      Sprint Pulse Cadence (days)
                    </label>
                    <input
                      data-testid="sprint-cadence-input"
                      type="number" min={7} max={30} value={form.sprint_pulse_cadence_days}
                      onChange={(e) => setForm({ ...form, sprint_pulse_cadence_days: parseInt(e.target.value) || 14 })}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                    />
                    <div className="font-ibm-plex text-[11px] text-slate-400 mt-1">
                      Pulse fires around sprint retrospectives. Default 14 days.
                    </div>
                  </div>
                )}

                {/* Experience Context Layer */}
                <div data-testid="experience-section" className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                      Total Professional Experience
                    </label>
                    <select
                      data-testid="total-experience-select"
                      value={form.total_experience}
                      onChange={(e) => setForm({ ...form, total_experience: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-ibm-plex bg-white focus:outline-none focus:ring-2 focus:ring-slate-900"
                    >
                      <option value="0-2">0–2 Years</option>
                      <option value="3-5">3–5 Years</option>
                      <option value="6-10">6–10 Years</option>
                      <option value="10+">10+ Years</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                      Relevant Experience (this domain)
                    </label>
                    <select
                      data-testid="relevant-experience-select"
                      value={form.relevant_experience}
                      onChange={(e) => setForm({ ...form, relevant_experience: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-ibm-plex bg-white focus:outline-none focus:ring-2 focus:ring-slate-900"
                    >
                      <option value="<6mo">Less than 6 months</option>
                      <option value="6-12mo">6–12 months</option>
                      <option value="1-3">1–3 years</option>
                      <option value="3+">3+ years</option>
                    </select>
                  </div>
                  <div className="md:col-span-2 font-ibm-plex text-[11px] text-slate-400">
                    Used by SYNQ Intelligence to weight signal confidence — never to suppress junior input.
                  </div>
                </div>

                {/* Survey Fatigue Indicator */}
                {fatigueLevel && (
                  <div data-testid="fatigue-indicator"
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                      fatigueLevel.level === "high"
                        ? "border-red-200 bg-red-50 text-red-700"
                        : fatigueLevel.level === "moderate"
                          ? "border-amber-200 bg-amber-50 text-amber-700"
                          : "border-emerald-200 bg-emerald-50 text-emerald-700"
                    }`}>
                    <Activity className="w-3.5 h-3.5" />
                    <span className="font-ibm-plex text-xs">
                      Survey fatigue: <span className="font-semibold">{fatigueLevel.label}</span>
                      {fatigueLevel.reduce_questions_by_pct > 0 && (
                        <span className="ml-1">— questions auto-reduced by {fatigueLevel.reduce_questions_by_pct}%</span>
                      )}
                    </span>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                    Project Name *
                  </label>
                  <input
                    data-testid="project-name-input"
                    type="text" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. ERP Migration 2025"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                    Description
                  </label>
                  <textarea
                    data-testid="project-description-input"
                    rows={2} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Brief description..."
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent resize-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">Project Type</label>
                    <select data-testid="project-type-select" value={form.project_type}
                      onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white">
                      {["IT", "Infrastructure", "Healthcare", "Finance", "Construction", "Startup", "Other"].map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">Team Size</label>
                    <input data-testid="team-size-input" type="number" min={1} value={form.team_size}
                      onChange={(e) => setForm({ ...form, team_size: parseInt(e.target.value) || 1 })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900" />
                  </div>
                  <div>
                    <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">Stakeholder Involvement</label>
                    <select data-testid="stakeholder-complexity-select" value={form.stakeholder_complexity}
                      onChange={(e) => setForm({ ...form, stakeholder_complexity: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white">
                      {["Low", "Medium", "High"].map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">Stress Level</label>
                    <select data-testid="stress-level-select" value={form.stress_level}
                      onChange={(e) => setForm({ ...form, stress_level: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white">
                      {["Low", "Medium", "High", "Critical"].map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">Deadline Pressure</label>
                    <select data-testid="deadline-pressure-select" value={form.deadline_pressure}
                      onChange={(e) => setForm({ ...form, deadline_pressure: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white">
                      {["Relaxed", "Normal", "Tight", "Critical"].map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">Timeline Pressure</label>
                    <select data-testid="timeline-pressure-select" value={form.timeline_pressure}
                      onChange={(e) => setForm({ ...form, timeline_pressure: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white">
                      {["Low", "Medium", "High"].map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 font-ibm-plex">
                {error}
              </div>
            )}

            <div className="flex gap-3 animate-slide-up stagger-3">
              <button type="button" onClick={() => setStep("role")}
                className="flex-1 border border-gray-300 text-slate-700 font-outfit font-semibold py-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button data-testid="next-to-assessment-btn" type="submit"
                className="flex-1 bg-slate-900 text-white font-outfit font-semibold py-3 rounded-lg hover:bg-slate-800 transition-colors flex items-center justify-center gap-2">
                Start Assessment <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ─── Step 3: One-Question-Per-Screen Assessment ────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Sticky Progress Header */}
      <div className="sticky top-14 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between mb-2">
            <div>
              <span className="font-outfit text-sm font-bold text-slate-900">{form.name}</span>
              <span className="font-ibm-plex text-xs text-slate-400 ml-2">— Assessment</span>
              {stageInfo?.mode_label && (
                <span data-testid="stage-mode-badge" className="font-ibm-plex text-[10px] uppercase tracking-widest ml-2 px-2 py-0.5 rounded-full bg-slate-900 text-white">
                  {stageInfo.mode_label}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="font-outfit text-sm font-bold text-slate-900">{answeredCount}/{totalQ}</span>
              <span className="font-ibm-plex text-xs text-slate-400">answered</span>
            </div>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-slate-900 rounded-full transition-all duration-300"
              style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Question navigation dots */}
        <div className="flex flex-wrap gap-1.5 mb-6 animate-slide-up">
          {questions.map((q, i) => (
            <button
              key={q.id}
              onClick={() => goToQuestion(i)}
              data-testid={`q-dot-${i}`}
              className={`w-6 h-6 rounded text-[10px] font-outfit font-bold transition-all ${
                i === currentQ
                  ? "bg-slate-900 text-white scale-110"
                  : responses[q.id]
                    ? "bg-emerald-500 text-white"
                    : "bg-gray-200 text-slate-400 hover:bg-gray-300"
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>

        {/* Current Question Card */}
        {currentQuestion && (
          <div className="animate-slide-up" key={currentQuestion.id}>
            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
              {/* Question number */}
              <div className="flex items-center justify-between mb-6">
                <span className="font-ibm-plex text-xs uppercase tracking-widest text-slate-400">
                  Question {currentQ + 1} of {totalQ}
                </span>
                {questionTimers[currentQuestion.id] && (
                  <span className="font-ibm-plex text-xs text-slate-300 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {questionTimers[currentQuestion.id]}s
                  </span>
                )}
              </div>

              {/* Question text */}
              <h2 className="font-outfit text-xl font-bold text-slate-900 leading-snug mb-8"
                data-testid="current-question-text">
                {currentQuestion.question}
              </h2>

              {/* Options */}
              <div className="space-y-3">
                {currentQuestion.options.map((opt) => {
                  const isSelected = responses[currentQuestion.id] === opt.label;
                  return (
                    <button
                      key={opt.label}
                      data-testid={`option-${currentQuestion.id}-${opt.label}`}
                      onClick={() => handleAnswer(currentQuestion.id, opt.label)}
                      className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all duration-150 flex items-start gap-4 group ${
                        isSelected
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-gray-200 hover:border-slate-300 hover:bg-gray-50 text-slate-700"
                      }`}
                    >
                      <span className={`flex-shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-outfit font-bold transition-colors ${
                        isSelected
                          ? "border-white text-white"
                          : "border-gray-300 text-slate-400 group-hover:border-slate-400"
                      }`}>
                        {opt.label}
                      </span>
                      <span className="font-ibm-plex text-base leading-relaxed pt-1">{opt.text}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-6">
              <button
                onClick={() => goToQuestion(Math.max(0, currentQ - 1))}
                disabled={currentQ === 0}
                className="flex items-center gap-2 font-outfit font-semibold text-sm text-slate-600 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Previous
              </button>

              {currentQ === totalQ - 1 && allAnswered ? (
                <button
                  data-testid="proceed-to-feedback-btn"
                  onClick={() => {
                    if (!allAnswered) { setError(`Please answer all ${totalQ} questions.`); return; }
                    setError("");
                    setStep("feedback");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  disabled={loading}
                  className="bg-slate-900 text-white font-outfit font-semibold px-6 py-3 rounded-lg hover:bg-slate-800 transition-colors flex items-center gap-2 disabled:opacity-60"
                >
                  Next: Open Feedback <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={() => goToQuestion(Math.min(totalQ - 1, currentQ + 1))}
                  disabled={currentQ === totalQ - 1}
                  data-testid="next-question-btn"
                  className="flex items-center gap-2 font-outfit font-semibold text-sm text-slate-600 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Completion hint */}
            {!allAnswered && currentQ === totalQ - 1 && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-700 font-ibm-plex flex items-center gap-2">
                <Info className="w-4 h-4 flex-shrink-0" />
                {totalQ - answeredCount} question{totalQ - answeredCount !== 1 ? "s" : ""} remaining. Navigate back to answer them.
              </div>
            )}

            {error && (
              <div data-testid="form-error" className="mt-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 font-ibm-plex">
                {error}
              </div>
            )}
          </div>
        )}

        {/* ─── Step 4: Open Feedback Intelligence ────────────────────────── */}
        {step === "feedback" && (
          <div data-testid="feedback-step" className="bg-white border border-gray-200 rounded-2xl p-8 animate-slide-up">
            <div className="mb-6">
              <h2 className="font-outfit text-2xl font-black text-slate-900">Open Feedback</h2>
              <p className="font-ibm-plex text-sm text-slate-500 mt-1">
                Two short questions to capture the emotional and contextual signal. Your responses are
                <span className="font-semibold text-slate-700"> anonymized</span> when surfaced to managers / leadership.
              </p>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                  What is one positive thing about the project currently?
                </label>
                <textarea
                  data-testid="positive-feedback-input"
                  rows={3}
                  maxLength={500}
                  required
                  value={form.positive_feedback}
                  onChange={(e) => setForm({ ...form, positive_feedback: e.target.value })}
                  placeholder="One thing that's working well right now…"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent resize-none"
                />
                <div className="font-ibm-plex text-[11px] text-slate-400 mt-1 text-right">
                  {form.positive_feedback.length} / 500
                </div>
              </div>

              <div>
                <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                  What is one area that needs improvement?
                </label>
                <textarea
                  data-testid="improvement-feedback-input"
                  rows={3}
                  maxLength={500}
                  required
                  value={form.improvement_feedback}
                  onChange={(e) => setForm({ ...form, improvement_feedback: e.target.value })}
                  placeholder="One thing the system should improve…"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent resize-none"
                />
                <div className="font-ibm-plex text-[11px] text-slate-400 mt-1 text-right">
                  {form.improvement_feedback.length} / 500
                </div>
              </div>
            </div>

            {error && (
              <div data-testid="feedback-error" className="mt-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 font-ibm-plex">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between mt-6">
              <button
                onClick={() => { setStep("assessment"); setError(""); }}
                data-testid="back-to-assessment-btn"
                className="flex items-center gap-2 font-outfit font-semibold text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back to questions
              </button>
              <button
                data-testid="analyze-project-btn"
                onClick={() => {
                  if (!form.positive_feedback.trim() || !form.improvement_feedback.trim()) {
                    setError("Please answer both open feedback questions.");
                    return;
                  }
                  submitAssessment();
                }}
                disabled={loading}
                className="bg-slate-900 text-white font-outfit font-semibold px-6 py-3 rounded-lg hover:bg-slate-800 transition-colors flex items-center gap-2 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>Generate Project IQ Report <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
